import { useTrackUserEvent } from "@/utils/analytics";
import { useCallback, useEffect, useState } from "react";
import { useActiveAccount } from "thirdweb/react";
import ConnectWalletButton from "../ConnectWalletButton";
import Note from "../Note";
import Button from "../SecondaryButton";
import StakeInput from "../StakeInput";
import {
  getWithdrawRequestDetails,
  requestWithdrawal,
} from "@/services/staking";
import Skeleton from "react-loading-skeleton";
import UnstakeSuccessfulModal from "../Modals/UnstakeSuccessfulModal";
import ErrorModal from "../Modals/ErrorModal";
import { NotificationType, showToastNotification } from "@/utils/notification";

import dayjs from "dayjs";
import { networkMap, NetworkType, networkType } from "@/config";

interface IProps {
  stSponBalance: number | null;
  isLoading: boolean;
}

const Unstaking = ({ stSponBalance, isLoading }: IProps) => {
  const trackUserEvent = useTrackUserEvent();
  const [unstakingSponAmount, setUnstakingSponAmount] = useState("");
  const [unstakingStatus, setUnstakingStatus] = useState<
    "" | "unstaking" | "success" | "error"
  >("");

  const [withdrawRequest, setWithdrawRequest] = useState<{
    unlockTime: number;
  } | null>(null);

  const activeAccount = useActiveAccount();

  const handleMaxClick = () => {
    setUnstakingSponAmount(stSponBalance?.toString() || "");
  };

  const errorMessage =
    stSponBalance !== null && Number(unstakingSponAmount) > stSponBalance
      ? `Your Available Balance is ${stSponBalance} stSPON`
      : "";

  const unstake = async () => {
    if (activeAccount) {
      const { blockExplorerUrls } = networkMap[networkType as NetworkType];
      setUnstakingStatus("unstaking");
      try {
        const txnHash = await requestWithdrawal(
          BigInt(Number(unstakingSponAmount) * 10 ** 18),
          activeAccount?.address,
          activeAccount
        );
        showToastNotification({
          title: "Your unstake request has been successfully submitted.",
          message: "",
          txnLink: `${blockExplorerUrls}/tx/${txnHash}`,
          timestamp: new Date(),
          type: NotificationType.Success,
        });
      } catch (e) {
        console.log("Error while unstaking: ", e);
        setUnstakingStatus("error");
        showToastNotification({
          title: "Your unstaking request couldn't be processed.",
          message: "Please try again or contact support if the issue persists.",
          timestamp: new Date(),
          type: NotificationType.Error,
        });
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));

      const event = new CustomEvent("refreshBalance");
      document?.dispatchEvent(event);
      const event2 = new CustomEvent("refreshStats");
      document?.dispatchEvent(event2);
      setUnstakingSponAmount("");
      setUnstakingStatus("success");
    }
  };

  useEffect(() => {
    if (!activeAccount?.address) setUnstakingSponAmount("");
  }, [activeAccount]);

  const fetchBalance = useCallback(async () => {
    if (activeAccount) {
      try {
        const [withdrawRequest] = await Promise.allSettled([
          getWithdrawRequestDetails(activeAccount),
        ]);

        if (withdrawRequest.status === "fulfilled") {
          setWithdrawRequest({
            unlockTime: Number(withdrawRequest.value[1]),
          });
        } else {
          setWithdrawRequest(null);
        }
      } catch (e) {
        console.log("error while fetching withdraw details: ", e);
      }
    }
  }, [activeAccount]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance, unstakingStatus]);

  let endTime = dayjs();
  let isInCooldown = false;
  if (withdrawRequest) {
    endTime = dayjs(withdrawRequest?.unlockTime * 1000);
    isInCooldown = endTime.isAfter(dayjs());
  }

  return (
    <div className="flex flex-col h-full justify-between">
      <div className="flex flex-col gap-[18px]">
        <div className="flex flex-col gap-3">
          <StakeInput
            value={
              Number(
                Number(unstakingSponAmount).toFixed(6) ?? "0"
              ).toString() || ""
            }
            onChange={(e) => setUnstakingSponAmount(e)}
            label="Unstake"
            unit="stSPON"
            error={Boolean(errorMessage)}
            disabled={unstakingStatus === "unstaking"}
          />
          <div className="flex justify-between items-center">
            {errorMessage ? (
              <div className="text-error font-medium text-sm flex gap-1">
                {errorMessage}
              </div>
            ) : (
              <div className="text-white font-medium text-sm flex gap-1">
                <span>Currently Staked:</span>
                {isLoading ? (
                  <Skeleton width="100px" />
                ) : (
                  <span>
                    {Number(stSponBalance?.toFixed(6) ?? "0").toString()} stSPON
                  </span>
                )}
              </div>
            )}
            <span
              onClick={handleMaxClick}
              className="bg-white/20 h-6 px-1.5 text-xs cursor-pointer items-center justify-center flex rounded-full"
            >
              MAX
            </span>
          </div>
        </div>
        {activeAccount?.address && (
          <>
            {withdrawRequest && isInCooldown ? (
              <Note
                title="⚠️️️ Important Note"
                description={
                  <>
                    Your unstaked tokens are in the{" "}
                    <span className="font-bold text-white">
                      14-day cooldown period
                    </span>
                    . After this period ends, you'll be able to withdraw your
                    SPON. Submitting another unstake request will reset your
                    cooldown period, starting from the time of your latest
                    request.
                  </>
                }
              />
            ) : (
              <Note
                title="⚠️ Cooldown Period Notice"
                description={
                  <>
                    Your unstaked tokens will be subject to a{" "}
                    <span className="font-bold text-white">
                      14-day cooldown period
                    </span>{" "}
                    before withdrawal is available.
                  </>
                }
              />
            )}
          </>
        )}
      </div>
      {activeAccount?.address ? (
        <Button
          onClick={() => {
            trackUserEvent("Unstake Tab- Unstake Click");
            unstake();
          }}
          loading={unstakingStatus === "unstaking"}
          disabled={
            unstakingStatus === "unstaking" ||
            !Number(unstakingSponAmount) ||
            Boolean(errorMessage)
          }
          className="mt-12"
          variant="light"
        >
          UNSTAKE
        </Button>
      ) : (
        <ConnectWalletButton />
      )}
      <UnstakeSuccessfulModal
        close={() => setUnstakingStatus("")}
        isOpen={unstakingStatus === "success"}
      />
      <ErrorModal
        close={() => setUnstakingStatus("")}
        isOpen={unstakingStatus === "error"}
        errorTitle="Oops! Something went wrong."
        errorDescription="Your unstaking request couldn't be processed. Please try again or contact support if the issue persists."
        handleTryAgain={() => {
          trackUserEvent("Unstaking Error Modal- Try Again Click");
          unstake();
        }}
      />
    </div>
  );
};

export default Unstaking;
